import axios from "axios";
import { config } from "@/config";
import type { Strings } from "@/strings";
import { googleLoginBody } from "./login-body";

const REDIRECT_PARAM_KEY = "redirectUrl";

const LOOPBACK_HOSTNAMES = new Set(["localhost", "127.0.0.1", "[::1]", "::1"]);

const isPrivateNetworkIP = (hostname: string): boolean => {
  // IPv4 private ranges (RFC 1918) + link-local
  const ipv4Match = hostname.match(/^(\d+)\.(\d+)\.(\d+)\.(\d+)$/);
  if (ipv4Match) {
    const [, a, b] = ipv4Match.map(Number);
    return (
      a === 10 ||                            // 10.0.0.0/8
      (a === 172 && b >= 16 && b <= 31) ||   // 172.16.0.0/12
      (a === 192 && b === 168) ||            // 192.168.0.0/16
      (a === 169 && b === 254)               // 169.254.0.0/16 (link-local)
    );
  }

  // IPv6 unique local (fc00::/7) and link-local (fe80::/10)
  // Only match bracketed IPv6 literals like [fe80::1], not domains like fd.example.com
  const ipv6Match = hostname.match(/^\[([0-9a-f:]+)\]$/i);
  if (!ipv6Match) return false;
  const addr = ipv6Match[1].toLowerCase();
  return (
    addr.startsWith("fc") ||
    addr.startsWith("fd") ||
    addr.startsWith("fe8") ||
    addr.startsWith("fe9") ||
    addr.startsWith("fea") ||
    addr.startsWith("feb")
  );
};

const toHostname = (hostOrUrl: string): string => {
  const normalized = hostOrUrl.trim().toLowerCase();

  try {
    // Supports values like:
    // - localhost
    // - localhost:3000
    // - [::1]:5173
    // - https://localhost:3000
    const value = normalized.includes("://")
      ? normalized
      : `http://${normalized}`;
    const hostname = new URL(value).hostname.toLowerCase();
    return hostname;
  } catch {
    return normalized;
  }
};

// RFC 6761 reserves `.localhost` for loopback, and browsers resolve every
// `*.localhost` name to 127.0.0.1 — so `maestro.localhost:5173` is as local
// as `localhost:5173`, and lets several apps run side by side by name.
const isLoopbackHost = (hostOrUrl: string): boolean => {
  const hostname = toHostname(hostOrUrl);
  return LOOPBACK_HOSTNAMES.has(hostname) || hostname.endsWith(".localhost");
};

const isDevLocal = (hostOrUrl: string): boolean => {
  if (import.meta.env.MODE !== "development") return false;
  const hostname = toHostname(hostOrUrl);
  return isLoopbackHost(hostname) || isPrivateNetworkIP(hostname);
};

const isAllowedRedirectDomain = (hostOrUrl: string): boolean => {
  const normalized = toHostname(hostOrUrl);

  if (isDevLocal(normalized)) {
    // Allow any loopback port in dev as long as loopback is allowlisted.
    return true;
  }

  const matchedDomain = config.allowedRedirectDomains.find((allowedDomain) => {
    const allowedHostname = toHostname(allowedDomain);
    return (
      normalized === allowedHostname ||
      normalized.endsWith(`.${allowedHostname}`)
    );
  });

  return Boolean(matchedDomain);
};

const getCandidateRedirectUrl = (): string => {
  const params = new URLSearchParams(window.location.search);

  const redirectUrl = params.get(REDIRECT_PARAM_KEY);
  if (!redirectUrl) {
    // return default redirect url if not present in the URL
    return config.redirectUrl;
  }

  return redirectUrl;
};

const buildSafeRedirectUrl = (): URL => {
  const candidate = getCandidateRedirectUrl();

  if (!candidate) {
    throw new Error("Redirect URL is not configured.");
  }

  let url: URL;

  try {
    url = new URL(candidate);
  } catch {
    throw new Error("Redirect URL is invalid.");
  }

  const devLocal = isDevLocal(url.hostname);

  if (devLocal) {
    if (url.protocol !== "http:" && url.protocol !== "https:") {
      throw new Error("Redirect URL must use HTTP or HTTPS.");
    }
  } else if (url.protocol !== "https:") {
    throw new Error("Redirect URL must use HTTPS.");
  }

  if (url.username || url.password) {
    throw new Error("Redirect URL must not contain credentials.");
  }

  if (!devLocal && url.port && url.port !== "443") {

    throw new Error("Redirect URL must use the default HTTPS port.");
  }

  if (!isAllowedRedirectDomain(url.hostname)) {
    throw new Error("Redirect domain is not allowed.");
  }

  return url;
};

/**
 * Cactus serves branch previews at `<app>-<branch-slug>.dev.vammo.com`, and the
 * repos enforce `type/ticket-description` branch names, so the slug always
 * reads `<type>-<ticket>-…` (`maestro-style-ops-0000-…`). The application is
 * the part before that — sending the whole label minted tokens carrying the
 * branch name in the `application` claim, and on admin accounts those ~50
 * extra bytes pushed the auth cookie past the browser's 4 KB limit: login
 * worked on maestro.dev and looped forever on branch DNSes.
 *
 * The ticket (`letters-digits`) is required by the pattern on purpose: an
 * ordinary application that happens to contain a type word (`foo-style-guide`)
 * must keep its full name.
 */
const BRANCH_SLUG_PATTERN =
  /^(.+?)-(feat|fix|style|refactor|chore|ci|test|perf|build|docs|revert)-[a-z]+-\d+(?:-.+)?$/;

export const getApplicationFromRedirect = (redirectUrl: URL): string => {
  const hostnameParts = redirectUrl.hostname.split(".");
  const label = hostnameParts[0];
  const branchMatch = label.match(BRANCH_SLUG_PATTERN);
  return branchMatch ? branchMatch[1] : label;
};

const redirectWithToken = (token: string, redirectUrl: URL): void => {
  // Since we can't set cookies on a different domain from JavaScript,
  // we'll pass the token as a URL parameter so the backend at redirectUrl
  // can read it and set the cookie in its own domain
  redirectUrl.searchParams.set("token", token);
  window.location.href = redirectUrl.toString();
};

type LoginRejection = {
  code?: string;
  application?: string;
  required_permissions?: string[];
};

/**
 * ms-login answers 401 for every rejection, so the code is the only way to tell
 * the three walls apart: wrong domain, no account, no permission. Saying which
 * one it is turns a dead end into something the person can act on — ask for an
 * account, or ask for a specific grant. An ms-login that sends no code falls
 * through to the generic line, so either side can ship first.
 */
export const describeLoginRejection = (
  data: unknown,
  strings: Strings
): string => {
  const { code, application, required_permissions: required } =
    (data as LoginRejection | undefined) ?? {};

  if (code === "EMAIL_OUTSIDE_INTERNAL_DOMAIN") {
    return strings.login401OutsideDomain;
  }

  if (code === "USER_NOT_REGISTERED") {
    return strings.login401NotRegistered;
  }

  if (code === "MISSING_PERMISSION") {
    // The body comes off the network — a truthy non-array has a .length but no
    // .join, which would throw instead of rendering the rejection.
    const permissions =
      Array.isArray(required) && required.length
        ? required.join(" or ")
        : undefined;

    return strings.login401MissingPermission(application, permissions);
  }

  return strings.login401Generic;
};

export const loginWithGoogle = async (
  googleToken: string,
  strings: Strings
): Promise<void> => {
  const redirectUrlObj = buildSafeRedirectUrl();
  const application = getApplicationFromRedirect(redirectUrlObj);

  try {
    const response = await axios.post(
      config.loginApiUrl,
      googleLoginBody(googleToken, application),
      {
        headers: {
          "Content-Type": "application/json",
          "Vammo-Application": application,
        },
      }
    );

    const { token } = response.data;

    if (!token) {
      throw new Error("No token received from server");
    }

    redirectWithToken(token, redirectUrlObj);
  } catch (error) {
    console.error("Login error:", error);
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      throw new Error(describeLoginRejection(error.response?.data, strings));
    }
    throw error;
  }
};

const PHONE_ENDPOINT_BASE = (): string => {
  // VITE_LOGIN_API_URL points to .../v2/login. The phone OTP endpoints live
  // at /v1/auth/phone/... on the same host, so derive the base from there.
  const loginUrl = new URL(config.loginApiUrl);
  return `${loginUrl.origin}/v1/auth/phone`;
};

const phoneHeaders = (): Record<string, string> => {
  const redirectUrlObj = buildSafeRedirectUrl();
  return {
    "Content-Type": "application/json",
    "Vammo-Application": getApplicationFromRedirect(redirectUrlObj),
  };
};

export interface SendOtpResponse {
  verification_token: string;
  expires_in: number;
}

export const sendWhatsAppOtp = async (
  phone: string,
  deviceFingerprint: string
): Promise<SendOtpResponse> => {
  // Validate redirect upfront so the user gets a clear error before sending the OTP
  buildSafeRedirectUrl();

  try {
    const response = await axios.post<SendOtpResponse>(
      `${PHONE_ENDPOINT_BASE()}/send`,
      { phone, device_fingerprint: deviceFingerprint },
      { headers: phoneHeaders() }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      const message = error.response?.data?.message;
      if (status === 422) {
        throw new Error(
          typeof message === "string"
            ? message
            : "Não foi possível enviar o código. Tente novamente em alguns instantes."
        );
      }
    }
    console.error("WhatsApp send error:", error);
    throw new Error(
      "Falha ao enviar o código por WhatsApp. Verifique sua conexão e tente novamente."
    );
  }
};

export interface VerifyOtpResult {
  status: "logged_in" | "signup_required" | "dni_required";
}

export const verifyWhatsAppOtp = async (
  verificationToken: string,
  code: string,
  deviceFingerprint: string,
  // CPF revalidation (HTTP 428 flow): re-verify with the SAME token + code
  // plus the CPF, digits only — ms-login keeps the challenge alive on the 428
  // exactly for this second call. Mirrors the mobile app's CpfConfirmation.
  dni?: string
): Promise<VerifyOtpResult> => {
  const redirectUrlObj = buildSafeRedirectUrl();

  try {
    const response = await axios.post(
      `${PHONE_ENDPOINT_BASE()}/verify`,
      {
        verification_token: verificationToken,
        code,
        device_fingerprint: deviceFingerprint,
        ...(dni ? { dni } : {}),
      },
      { headers: phoneHeaders() }
    );

    const { token, is_signup } = response.data ?? {};

    if (is_signup) {
      return { status: "signup_required" };
    }

    if (!token) {
      throw new Error("No token received from server");
    }

    redirectWithToken(token, redirectUrlObj);
    return { status: "logged_in" };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      const data = error.response?.data;
      const message = data?.message;

      if (status === 428 || data?.code === "DNI_CONFIRMATION_REQUIRED") {
        return { status: "dni_required" };
      }
      if (status === 422) {
        throw new Error(
          typeof message === "string"
            ? message
            : "Código inválido ou expirado. Solicite um novo código."
        );
      }
    }
    console.error("WhatsApp verify error:", error);
    throw new Error(
      "Falha ao verificar o código. Tente novamente em alguns instantes."
    );
  }
};
